import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Callout, Spinner, Table, type TableColumn } from '@humanforce/mango-ui-react';
import { PageTitle } from '../components/layout/PageTitle';
import { getWorkersPage, type HrWorkerPersonal, type HrWorkersPage } from '../features/workers/workerDirectoryApi';
import { useWorkerPersonalMemory } from '../features/workers/WorkerPersonalMemory';
import { observer } from 'mobx-react-lite';
import { userStore } from '../auth/store';

type Row = HrWorkerPersonal & Record<string, unknown>;

const show = (value: string | null | undefined): string => value?.trim() || '—';
const workerName = (person: HrWorkerPersonal): string =>
  [person.firstName, person.lastName].filter(Boolean).join(' ').trim() || '—';

export const WorkerListPage = observer(() => <WorkerListContent key={`${window.location.host}:${userStore.authEpoch}`} />);

const WorkerListContent = () => {
  const navigate = useNavigate();
  const memory = useWorkerPersonalMemory();
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<HrWorkersPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);
    void getWorkersPage(page).then((response) => {
      if (ignore) return;
      if (response.ok && response.data) setResult(response.data);
      else { setResult(null); setError(response.message ?? 'Could not load workers.'); }
      setLoading(false);
    });
    return () => { ignore = true; };
  }, [page]);

  const columns: TableColumn[] = [
    { field: 'firstName', header: 'Worker', body: (row: Row) => (
      <div className="mgo-stack mgo-gap-2xs">
        <span className="mgo-text-s mgo-weight-strong">{workerName(row)}</span>
        {row.preferredName && <span className="mgo-text-xs mgo-color-muted">Preferred: {row.preferredName}</span>}
      </div>
    ) },
    { field: 'employeeNumber', header: 'Employee number', body: (row: Row) => show(row.employeeNumber) },
    { field: 'dateOfBirth', header: 'Date of birth', body: (row: Row) => show(row.dateOfBirth?.slice(0, 10)) },
    { field: 'actions', header: 'Actions', body: (row: Row) => (
      <Button variant="secondary" size="small" label="Open record" onClick={() => {
        memory.remember(row);
        navigate(`/workers/worker-record/personal?worker=${encodeURIComponent(row.personId)}`);
      }} />
    ) },
  ];

  return (
    <>
      <PageTitle title="Workers" subtitle="HR worker directory" />
      {error && <Callout tone="critical" title="Couldn't load workers">{error}</Callout>}
      {loading ? (
        <div className="mgo-cluster mgo-gap-s mgo-align-center"><Spinner /> Loading workers…</div>
      ) : result?.people.length ? (
        <Table value={result.people as Row[]} columns={columns} testId="workers-table" />
      ) : !error && <Callout tone="neutral" title="No workers found">There are no workers on this page.</Callout>}
      <div className="mgo-cluster mgo-justify-end mgo-gap-s mgo-align-center">
        <span className="mgo-text-xs mgo-color-muted">Page {result?.currentPage ?? page} of {result?.totalPages ?? '—'} · {result?.total ?? 0} workers · at most 50 per page</span>
        <Button variant="secondary" size="small" label="Previous" disabled={page === 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))} />
        <Button variant="secondary" size="small" label="Next" disabled={loading || !result?.hasMore} onClick={() => setPage((value) => value + 1)} />
      </div>
    </>
  );
};
